import './HashCode.css';

import { Alert, Button, Col, Container, Form, Overlay, Row, Tooltip } from 'react-bootstrap';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Base64 } from 'js-base64';
import Icon from '@amzn/meridian/icon';
import _ from 'lodash';
import copyTokens from '@amzn/meridian-tokens/base/icon/copy';
import murmurHash3 from 'murmurhash3js';
import stringify from 'json-stable-stringify';

const base64Murmur3x64_128HashOf = (input, rotateBits) => {
  const hexString = murmurHash3.x64.hash128(input);
  const hexBytes = new Uint8Array(hexString.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
  let hashBytes;
  if (rotateBits) {
    hashBytes = new Uint8Array([...hexBytes.slice(0, 8).reverse(), ...hexBytes.slice(8).reverse()]);
  } else {
    hashBytes = hexBytes;
  }
  return Base64.fromUint8Array(hashBytes, false);
};

const HashCode = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [formattedJsonOutput, setFormattedJsonOutput] = useState('');
  const [hashOutput, setHashOutput] = useState('');
  const [formattedJsonAsEscapedString, setFormattedJsonAsEscapedString] = useState(false);
  const [fixEndianness, setFixEndianness] = useState(
    base64Murmur3x64_128HashOf(
      '{"description":"used for integration test for RTS team","name":"RTS_TEST","tenantId":"RTS_TEST"}',
      true
    ) === 'oPLFMtfSpCS4MgjpaN+V0w=='
  );
  const [showFormattedJsonCopyTooltip, setShowFormattedJsonCopyTooltip] = useState(false);
  const [showHashOutputCopyTooltit, setShowHashOutputCopyTooltip] = useState(false);

  const formattedJsonCopyTooltipTarget = useRef(null);
  const hashOutputCopyTooltipTarget = useRef(null);

  const setJsonInputHandlerDebounced = useMemo(() => _.debounce(e => setJsonInput(e.target.value), 200), []);

  useEffect(() => {
    if (showFormattedJsonCopyTooltip === true) {
      const timer = setTimeout(() => setShowFormattedJsonCopyTooltip(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showFormattedJsonCopyTooltip]);

  useEffect(() => {
    if (showHashOutputCopyTooltit === true) {
      const timer = setTimeout(() => setShowHashOutputCopyTooltip(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showHashOutputCopyTooltit]);

  useEffect(() => {
    if (jsonInput === undefined || jsonInput === '') {
      setFormattedJsonOutput('');
      setHashOutput('');
      return;
    }

    try {
      const sortedJson = stringify(JSON.parse(jsonInput));

      const hashString = base64Murmur3x64_128HashOf(sortedJson, fixEndianness);

      if (formattedJsonAsEscapedString === true) {
        const escaped = sortedJson.replace(/"/g, '\\"');
        setFormattedJsonOutput(`"${escaped}"`);
      } else {
        setFormattedJsonOutput(sortedJson);
      }
      setHashOutput(hashString);
    } catch (e) {
      console.log(e);
      setFormattedJsonOutput('Invalid json input');
      setHashOutput('');
    }
  }, [jsonInput, formattedJsonAsEscapedString, fixEndianness]);

  const copyFormattedJsonToClipboard = () => {
    navigator.clipboard
      .writeText(formattedJsonOutput)
      .then(() => setShowFormattedJsonCopyTooltip(true))
      .catch(e => console.log(`Failed to copy formatted json to clipboard: ${e}`));
  };

  const copyHashToClipboard = () => {
    navigator.clipboard
      .writeText(hashOutput)
      .then(() => setShowHashOutputCopyTooltip(true))
      .catch(e => console.log(`Failed to copy hash output to clipboard: ${e}`));
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <Form.Label>
            Restructure json and get its murmur3 hash. Should output the same thing that RosettaTranslationTenantService
            does internally.
            <Alert variant="warning">
              This component self-corrects it's output ('Fix endianness' flag), but if you want to make double sure,
              then here is the self test: for input{' '}
              <code>
                {'{"description":"used for integration test for RTS team","name":"RTS_TEST","tenantId":"RTS_TEST"}'}{' '}
              </code>
              expected output is <code>oPLFMtfSpCS4MgjpaN+V0w==</code>.
            </Alert>
          </Form.Label>
        </Col>
      </Row>
      <Row className="mt-1 mb-1">
        <Col>
          <Form.Control
            placeholder="Paste json here"
            as="textarea"
            rows="5"
            onChange={e => {
              e.persist();
              setJsonInputHandlerDebounced(e);
            }}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <Container fluid className="p-2">
            <Row>
              <Form.Check
                type="checkbox"
                checked={formattedJsonAsEscapedString}
                onChange={e => setFormattedJsonAsEscapedString(e.target.checked)}
                label="Formated JSON as escaped string?"
              />
            </Row>
            <Row>
              <Form.Check
                type="checkbox"
                checked={fixEndianness}
                onChange={e => setFixEndianness(e.target.checked)}
                label="Fix endianness (rotate bits)?"
              />
            </Row>
            <Row className="mt-1 mb-1 static-height grey-border">
              <Col>
                <div>
                  <code>{formattedJsonOutput}</code>
                  <Button
                    className="position-absolute left-92p-top-5p on-top"
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => copyFormattedJsonToClipboard()}
                    ref={formattedJsonCopyTooltipTarget}
                  >
                    <Icon tokens={copyTokens}></Icon>
                  </Button>
                  <Overlay
                    target={formattedJsonCopyTooltipTarget.current}
                    show={showFormattedJsonCopyTooltip}
                    placement="left"
                  >
                    {props => (
                      <Tooltip id="copied-tooltip" {...props}>
                        Copied!
                      </Tooltip>
                    )}
                  </Overlay>
                </div>
              </Col>
            </Row>
            <Row className="mt-1 mb-1 static-height grey-border">
              <Col>
                <div>
                  <code>{hashOutput}</code>
                  <Button
                    className="position-absolute left-92p-top-5p on-top"
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => copyHashToClipboard()}
                    ref={hashOutputCopyTooltipTarget}
                  >
                    <Icon tokens={copyTokens}></Icon>
                  </Button>
                  <Overlay
                    target={hashOutputCopyTooltipTarget.current}
                    show={showHashOutputCopyTooltit}
                    placement="left"
                  >
                    {props => (
                      <Tooltip id="copied-tooltip" {...props}>
                        Copied!
                      </Tooltip>
                    )}
                  </Overlay>
                </div>
              </Col>
            </Row>
          </Container>
        </Col>
      </Row>
    </Container>
  );
};

export default HashCode;
